import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IAccomplishment } from '@app/shared/interfaces/IAccomplishment';
import { IAlternative } from '@app/shared/interfaces/IAlternative';
import { IAWP } from '@app/shared/interfaces/IAWP';
import { ICapitalProposal } from '@app/shared/interfaces/ICapitalProposal';
import { ICLEARUser } from '@app/shared/interfaces/ICLEARUser';
import { ICMF } from '@app/shared/interfaces/ICMF';
import { ICollisionDiagram } from '@app/shared/interfaces/ICollisionDiagram';
import { IContributingFactor } from '@app/shared/interfaces/IContributingFactor';
import { IContributingFactorCategory } from '@app/shared/interfaces/IContributingFactorCategory';
import { ICountermeasure } from '@app/shared/interfaces/ICountermeasure';
import { ICountermeasureCost } from '@app/shared/interfaces/ICountermeasureCost';
import { ICountermeasureLocation } from '@app/shared/interfaces/ICountermeasureLocation';
import { ICrashCost } from '@app/shared/interfaces/ICrashCost';
import { ICrashCounts } from '@app/shared/interfaces/ICrashCounts';
import { ICrashLocationDetail } from '@app/shared/interfaces/ICrashLocationDetail';
import { ICrashStatistic } from '@app/shared/interfaces/ICrashStatistic';
import { ICrashTypeCategory } from '@app/shared/interfaces/ICrashTypeCategory';
import { IFacilityType } from '@app/shared/interfaces/IFacilityType';
import { IImplementationOverviewMetric } from '@app/shared/interfaces/IImplementationOverviewMetric';
import {
  IInvestigation,
  IInvestigationNote,
  IInvestigationToUser,
  IUpdateInvestigationApprovalResource,
} from '@app/shared/interfaces/IInvestigation';
import { IJurisdiction } from '@app/shared/interfaces/IJurisdiction';
import { ILinkedCountermeasure } from '@app/shared/interfaces/ILinkedCountermeasure';
import {
  IAdminContacts,
  IChannel,
  IInvestigationRegionStats,
  INewsText,
} from '@app/shared/interfaces/INews';
import {
  INSIntersectionResult,
  INSRampResult,
  INSResults,
  INSSegmentResult,
} from '@app/shared/interfaces/INSResults';
import { IRecommendation } from '@app/shared/interfaces/IRecommendation';
import { IRefCountermeasure } from '@app/shared/interfaces/IRefCountermeasure';
import { ISavedQuery } from '@app/shared/interfaces/ISavedQuery';
import {
  BasicSegmentInfo,
  IIntersectionVolume,
  ISite,
} from '@app/shared/interfaces/ISite';
import {
  SPFInfo,
  refStatewideAverages,
  refStatewideProportions,
} from '@app/shared/interfaces/ISPFInfo';
import { IStudyArea } from '@app/shared/interfaces/IStudyArea';
import { ISummaryStatisticGroup } from '@app/shared/interfaces/ISummaryStatisticGroup';
import {
  ICLEARSafety_AppUpdates,
  ISystemSetting,
} from '@app/shared/interfaces/ISystemSetting';
import { ITestFrequency } from '@app/shared/interfaces/ITestFrequency';
import { ITestProportions } from '@app/shared/interfaces/ITestProportion';
import { IVehicleDetail } from '@app/shared/interfaces/IVehicleDetail';
import { IUserWithID } from '@app/shared/misc/utils';
import { AnnualScreening } from '@app/shared/models/AnnualScreening';
import {
  IAnnualResults,
  IRegionAnnualScreening,
  IScenarioToUser,
  Scenario,
  ScenarioToUser,
} from '@app/shared/models/Scenario';

import{
  InvestigationToUser
} from '@app/shared/interfaces/IInvestigation';
import { ScenarioRunHistory } from '@app/shared/models/ScenarioRunHistory';
import { environment as env } from '@env/environment';
import { shareReplay, take } from 'rxjs/operators';
import { JwtService } from './jwt.service';
import { IRefFacilityType } from '@app/shared/interfaces/IRefFacilityType';

@Injectable()
export class ApiService {
  constructor(private http: HttpClient, private jwt: JwtService) {}

  get GlobalHeaders() {
    return this.jwt.getJWThttpHeaders(true);
  }
  get Headers() {
    return this.jwt.getJWThttpHeaders(false);
  }

  //#region Investigations
  getAllInvestigations() {
    const endPoint = `${env.api}/investigations`;
    return this.http.get<IInvestigation[]>(endPoint);
  }
  //#endregion

  healthCheck() {
    return this.http.get(`${env.health}`, { responseType: 'text' });
  }

  GetRecentUpdates() {
    return this.http.get<ICLEARSafety_AppUpdates[]>(
      `${env.api}/settings/GetRecentUpdates`
    );
  }

  getCDVQueryLuts() {
    const endPoint = `${env.cdvquery}`;
    return this.http.get(endPoint, { headers: this.GlobalHeaders });
  }

  getUserByName(name: string) {
    const endPoint = `${env.api}/users/${name}`;
    return this.http.get<ICLEARUser>(endPoint);
  }

  getLocalUsers() {
    const endPoint = `${env.api}/users/list/local`;
    return this.http.get<ICLEARUser[]>(endPoint);
  }

  getCounties() {
    const endPoint = `${env.crashref}/Counties`;
    return this.http.get(endPoint, { headers: this.GlobalHeaders });
  }

  getMunis(fipsCode: string) {
    const endPoint = `${env.crashref}/GetMunis/?countyFips=${fipsCode}`;
    return this.http.get(endPoint, {
      headers: this.GlobalHeaders,
    });
  }

  getDOTRegions() {
    const endPoint = `${env.api}/Reference/DOTRegions`;
    return this.http.get<any[]>(endPoint, {
      headers: this.Headers,
    });
  }

  getDOTRegionSettings() {
    const endPoint = `${env.api}/AWP/DOTRegionSettings`;
    return this.http.get<any[]>(endPoint, {
      headers: this.Headers,
    });
  }

  getMPORegions() {
    const endPoint = `${env.api}/Reference/MPORegions`;
    return this.http.get<any[]>(endPoint, {
      headers: this.Headers,
    });
  }

  getStreetsInMuni(muniFIPS: string) {
    const endPoint = `${env.gapi_root}roadInventory/GetStreetListByCity/${muniFIPS}`;
    return this.http.get(endPoint, {
      headers: this.GlobalHeaders,
    });
  }

  getIntersectingStreets(streetListID: string, muniFIPS: string) {
    const endPoint = `${env.gapi_root}roadInventory/GetIntersectingStreet/${streetListID}/${muniFIPS}`;
    return this.http.get(endPoint, {
      headers: this.GlobalHeaders,
    });
  }

  getIntersectionPoint(args: string) {
    return this.http
      .get<any>(
        env.geocoderUrl +
          '?' +
          args +
          '&f=pjson&token=' +
          sessionStorage.getItem('PortalToken')
      )
      .toPromise();
  }

  getrefCrashTypeCategory() {
    const endPoint = `${env.api}/Reference/GetrefCrashTypeCategory`;
    return this.http.get<any[]>(endPoint, {
      headers: this.Headers,
    });
  }

  getAllScenarios() {
    const endPoint = `${env.api}/scenarios`;
    return this.http.get<Scenario[]>(endPoint, {
      headers: this.Headers,
    });
  }

  checkScenarioName(name: string) {
    const endPoint = `${env.api}/scenarios/checkName?name=${name}`;
    return this.http.get<boolean>(endPoint, {
      headers: this.Headers,
    });
  }

  getAdHocScenarios() {
    const endPoint = `${env.api}/scenarios/adhoc`;
    return this.http.get<Scenario[]>(endPoint, {
      headers: this.Headers,
    });
  }

  saveNewScenario(scenario: Scenario) {
    const endPoint = `${env.api}/scenarios`;
    return this.http.post<Scenario>(endPoint, scenario, {
      headers: this.Headers,
    });
  }

  deleteScenario(scenarioId: number) {
    const endPoint = `${env.api}/scenarios/${scenarioId}`;
    return this.http.delete<Scenario>(endPoint, {
      headers: this.Headers,
    });
  }

  updateScenario(scenario: Scenario) {
    const endPoint = `${env.api}/scenarios/${scenario.ScenarioID}`;
    return this.http.put<Scenario>(endPoint, scenario, {
      headers: this.Headers,
    });
  }

  updateScenarioName(scenario: Scenario) {
    const endPoint = `${env.api}/scenarios/Name/${scenario.ScenarioID}`;
    return this.http.put<Scenario>(endPoint, scenario, {
      headers: this.Headers,
    });
  }

  getJurisdictions() {
    const endPoint = `${env.api}/scenarios/jurisdictions`;
    return this.http.get<IJurisdiction[]>(endPoint, {
      headers: this.Headers,
    });
  }

  updateStep(investigationId: number, stepNumber: number) {
    const endPoint = `${env.api}/investigations/${investigationId}/updateStep/${stepNumber}`;
    return this.http.get<IInvestigation>(endPoint);
  }

  updateRunHistory(scenarioRunHistory: ScenarioRunHistory) {
    const endPoint = `${env.api}/runhistories/${scenarioRunHistory.ScenarioRunHistoryID}`;
    return this.http.put<ScenarioRunHistory>(endPoint, scenarioRunHistory);
  }

  getScenario(scenarioID: number) {
    const endPoint = `${env.api}/scenarios/${scenarioID}`;
    return this.http.get<Scenario>(endPoint, {
      headers: this.Headers,
    });
  }

  runScenario(scenarioId: number) {
    const endPoint = `${
      env.screeningUrl
    }?ScenarioID=${scenarioId}&f=json&token=${sessionStorage.getItem(
      'PortalToken'
    )}`;
    return this.http.get(endPoint);
  }

  getResults(scenarioId: number) {
    const endPoint = `${env.api}/scenarios/${scenarioId}/results`;
    return this.http.get<INSResults>(endPoint, {
      headers: this.Headers,
    });
  }

  ShareScenario(AddUserToScenario: ScenarioToUser) {
    const endPoint = `${env.api}/scenarios/SaveScenarioToUser`;
    return this.http.post<IScenarioToUser>(endPoint, AddUserToScenario, {
      headers: this.Headers,
    });
  }

  ShareInvestigation(AddUserToInvestigation: InvestigationToUser) {
    const endPoint = `${env.api}/investigations/SaveInvestigationToUser`;
    return this.http.post<IInvestigationToUser>(endPoint, AddUserToInvestigation, {
      headers: this.Headers,
    });
  }

  RemoveInvestigation(RemoveInvestigationToUser: InvestigationToUser) {
    const endPoint = `${env.api}/investigations/RemoveInvestigationToUser`;
    return this.http.post<IInvestigationToUser>(endPoint, RemoveInvestigationToUser, {
      headers: this.Headers,
    });
  }

  getResultsForRunHistory(scenarioId: number, runHistoryId: number) {
    const endPoint = `${env.api}/scenarios/${scenarioId}/Results/${runHistoryId}`;
    return this.http.get<INSResults>(endPoint, {
      headers: this.Headers,
    });
  }

  getSegmentResult(screenedSegmentID: number) {
    const endPoint = `${env.api}/scenarios/segmentResult/${screenedSegmentID}`;
    return this.http.get<INSSegmentResult>(endPoint, {
      headers: this.Headers,
    });
  }

  getIntersectionResult(screenedIntersectionID: number) {
    const endPoint = `${env.api}/scenarios/intersectionResult/${screenedIntersectionID}`;
    return this.http.get<INSIntersectionResult>(endPoint, {
      headers: this.Headers,
    });
  }

  getRampResult(screenedRampID: number) {
    const endPoint = `${env.api}/scenarios/rampResult/${screenedRampID}`;
    return this.http.get<INSRampResult>(endPoint, {
      headers: this.Headers,
    });
  }

  getWorkingAWPsForYear(
    year: number,
    ScenarioID: number,
    ScenarioRunHistoryID: number
  ) {
    const endPoint = `${env.api}/awp/working/${year}/${ScenarioID}/${ScenarioRunHistoryID}`;
    return this.http.get<IAWP[]>(endPoint, {
      headers: this.Headers,
    });
  }

  unpublishAnnualScreening(annualScreeningID: number) {
    const endPoint = `${env.api}/scenarios/annualscreening/${annualScreeningID}`;
    return this.http.delete<Scenario>(endPoint, {
      headers: this.Headers,
    });
  }

  checkAnnualScreeningYear(year: number, scenarioID: number) {
    const endPoint = `${env.api}/scenarios/annualscreening/checkyear/${year}/${scenarioID}`;
    return this.http.get<Scenario>(endPoint, {
      headers: this.Headers,
    });
  }

  publishAnnualScreening(annualScreening: AnnualScreening) {
    const endPoint = `${env.api}/scenarios/annualscreening`;
    return this.http.post<Scenario>(endPoint, annualScreening, {
      headers: this.Headers,
    });
  }

  getCrashes(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/crashes`;
    return this.http.get<any[]>(endPoint);
  }

  GetTestOfProportionCrashes(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/TestOfProportionCrashes`;
    return this.http.get<any[]>(endPoint);
  }

  getCrashesWithNarrative(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/crashesWithNarrative`;
    return this.http.get<any[]>(endPoint);
  }

  getCrashesBySegment(
    investigationID: number,
    segmentID: number,
    screened: boolean = false
  ) {
    const endPoint = `${env.api}/investigations/${investigationID}/segments/${segmentID}?screened=${screened}`;
    return this.http.get<any[]>(endPoint);
  }

  getCrashesBySegmentWithGeometry(investigationID: number, ISites: ISite) {
    let SegmentInfo: BasicSegmentInfo = {
      ID: ISites.ID,
      SegmentID: ISites.SegmentID,
      Geometry: ISites.graphic
        ? (ISites.graphic.geometry as __esri.Polyline)
        : null,
    } as BasicSegmentInfo;
    const endPoint = `${env.api}/investigations/${investigationID}/segments`;
    return this.http.post<any[]>(endPoint, SegmentInfo);
  }

  getCrashesByIntersection(
    investigationID: number,
    intersectionID: number,
    screened: boolean = false
  ) {
    const endPoint = `${env.api}/investigations/${investigationID}/intersections/${intersectionID}?screened=${screened}`;
    return this.http.get<any[]>(endPoint);
  }

  getCrashesByRamp(
    investigationID: number,
    rampID: number,
    screened: boolean = false
  ) {
    const endPoint = `${env.api}/investigations/${investigationID}/ramps/${rampID}?screened=${screened}`;
    return this.http.get<any[]>(endPoint);
  }

  getCrashesByEventSegment(
    investigationID: number,
    EventSegmentID: number,
    screened: boolean = false
  ) {
    const endPoint = `${env.api}/investigations/${investigationID}/EventSegments/${EventSegmentID}?screened=${screened}`;
    return this.http.get<any[]>(endPoint);
  }

  getStatewideProportions(
    facilityTypeID: number,
    fromYear: number,
    toYear: number
  ) {
    const endPoint = `${env.api}/investigations/statewideproportions/${facilityTypeID}/${fromYear}/${toYear}`;
    return this.http.get<any[]>(endPoint);
  }

  getCrashNarrative(caseNumber: string) {
    const endPoint = `${env.gapi_root}crashref/GetCrashNarrative/${caseNumber}`;
    return this.http.get<any>(endPoint, {
      headers: this.GlobalHeaders,
    });
  }

  getCrashDiagram(caseNumber: string) {
    const endPoint = `${env.gapi_root}crashref/GetCrashDiagram/${caseNumber}`;
    return this.http.get<any>(endPoint, {
      headers: this.GlobalHeaders,
    });
  }

  saveNewInvestigation(investigation: IInvestigation) {
    const endPoint = `${env.api}/investigations`;
    return this.http.post<IInvestigation>(endPoint, investigation);
  }

  saveNewCountermeasure(countermeasure: IRefCountermeasure) {
    const endPoint = `${env.api}/investigations/countermeasures`;
    return this.http.post<IRefCountermeasure>(endPoint, countermeasure);
  }

  getUserDefinedCountermeasures() {
    const endPoint = `${env.api}/investigations/userdefinedcountermeasures`;
    return this.http.get<IRefCountermeasure[]>(endPoint);
  }

  updateInvestigation(investigation: IInvestigation) {
    const endPoint = `${env.api}/investigations/${investigation.InvestigationID}`;
    return this.http.put<IInvestigation>(endPoint, investigation);
  }

  updateInvestigationSite(investigationSite: ISite) {
    const endPoint = `${env.api}/investigations/updateInvestigationSite`;
    return this.http.put<any>(endPoint, investigationSite);
  }

  getSharedInvestigations(CLEARUserID: number, clearUserName: string){
    const endPoint = `${env.api}/investigations/SharedInvestigations`;
    
    return this.http.get<any[]>(endPoint, {
      params: {
        clearUserId: CLEARUserID.toString(),
        clearUserName: clearUserName,
      },
    });
  }

  updateInvestigationApproval(
    investigationID: number,
    UpdateInvestigationApprovalResource: IUpdateInvestigationApprovalResource
  ) {
    const endPoint = `${env.api}/investigations/${investigationID}`;
    return this.http.patch<any>(endPoint, UpdateInvestigationApprovalResource);
  }

  getInvestigation(id: number | string) {
    const endPoint = `${env.api}/investigations/${id}`;
    return this.http.get<IInvestigation>(endPoint);
  }

  getStudyAreaForMap(IDList: number[]) {
    const endPoint = `${env.api}/investigations/GetStudyAreaForMap`;
    return this.http.post<IInvestigation[]>(endPoint, IDList);
  }

  deleteInvestigation(id: number) {
    const endPoint = `${env.api}/investigations/${id}`;
    return this.http.delete(endPoint);
  }

  cloneInvestigation(id: number, user: string, investigationName?: string) {
    const endPoint = `${env.api}/investigations/${id}/${user}/clone`;
    const headers = { 'Content-Type': 'application/json' };
    return this.http.post(endPoint, JSON.stringify(investigationName || ''), {
      headers,
    });
  }

  saveLinkedCountermeasure(countermeasure: ILinkedCountermeasure) {
    const endPoint = `${env.api}/investigations/linkedcountermeasures`;
    return this.http.post<ILinkedCountermeasure>(endPoint, countermeasure);
  }

  removeRecommendation(id: number) {
    const endPoint = `${env.api}/investigations/${id}/recommendations`;
    return this.http.delete(endPoint);
  }

  saveRecommendation(recommendation: IRecommendation, id: number) {
    const endPoint = `${env.api}/investigations/${id}/recommendations`;
    return this.http.post<IRecommendation>(endPoint, recommendation);
  }

  updateRecommendation(
    invesId: number,
    recommendation: IRecommendation,
    recId: number
  ) {
    const endPoint = `${env.api}/investigations/${invesId}/recommendations/${recId}`;
    return this.http.put<IRecommendation>(endPoint, recommendation);
  }

  updateLinkedCountermeasure(countermeasures: ILinkedCountermeasure[]) {
    const endPoint = `${env.api}/investigations/linkedcountermeasures`;
    return this.http.put<ILinkedCountermeasure[]>(endPoint, countermeasures);
  }

  getLinkedCountermeasures(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/linkedcountermeasures`;
    return this.http.get<ILinkedCountermeasure[]>(endPoint);
  }

  getLinkedCountermeasuresForAlternatives(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/alternativelinkedcountermeasures/`;
    return this.http.get<ILinkedCountermeasure[]>(endPoint);
  }

  getLinkedCountermeasureLocationsByInvestigation(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/linkedcountermeasures/location`;
    return this.http.get<ICountermeasureLocation>(endPoint);
  }

  // used for getting contributing category/crash pattern ID
  getCountermeasureDetail(linkedCountermeasureID: number) {
    const endPoint = `${env.api}/investigations/countermeasures/${linkedCountermeasureID}`;
    return this.http.get<any[]>(endPoint);
  }

  removeCountermeasure(linkedCountermeasureID: number) {
    const endPoint = `${env.api}/investigations/linkedcountermeasures/${linkedCountermeasureID}`;
    return this.http.delete(endPoint);
  }

  getCountermeasureLocation(linkedCountermeasureID: number) {
    const endPoint = `${env.api}/investigations/linkedcountermeasures/${linkedCountermeasureID}/location`;
    return this.http.get<ICountermeasureLocation>(endPoint);
  }

  saveCountermeasureLocation(location: ICountermeasureLocation) {
    const endPoint = `${env.api}/investigations/linkedcountermeasures/location`;
    return this.http.post(endPoint, location);
  }

  updateCountermeasureLocation(
    linkedCountermeasureID: number,
    location: ICountermeasureLocation
  ) {
    const endPoint = `${env.api}/investigations/linkedcountermeasures/${linkedCountermeasureID}/location`;
    return this.http.put(endPoint, location);
  }

  getCountermeasureLocationCrashCounts(
    investigationID: number,
    geometry: __esri.Geometry,
    noBufferGeometry: __esri.Geometry
  ) {
    const endPoint = `${env.api}/investigations/${investigationID}/linkedcountermeasures/location/crashcounts`;
    return this.http.post<ICrashCounts>(endPoint, {
      geometry: geometry,
      noBufferGeometry: noBufferGeometry,
    });
  }

  getSitesCrashCounts(investigationID: number, ISites: ISite[]) {
    let SegmentInfo: BasicSegmentInfo[] = [];
    ISites.forEach((site) => {
      if (site.SegmentID) {
        SegmentInfo.push({
          ID: site.ID,
          SegmentID: site.SegmentID,
          Geometry: site.graphic
            ? (site.graphic.geometry as __esri.Polyline)
            : null,
        });
      }
    });
    const endPoint = `${env.api}/investigations/${investigationID}/location/crashcounts`;
    return this.http.post<ISite[]>(endPoint, SegmentInfo);
  }

  saveContributingFactors(investigationId: number, contributingFactors: any) {
    const endPoint = `${env.api}/investigations/${investigationId}/contributingfactors`;
    return this.http.post<any[]>(endPoint, contributingFactors);
  }

  saveCrashPatterns(investigationId: number, crashPatterns: any) {
    const endPoint = `${env.api}/investigations/${investigationId}/crashpatterns`;
    return this.http.post<any[]>(endPoint, crashPatterns);
  }

  getContributingFactors(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/contributingfactors`;
    return this.http.get<any>(endPoint);
  }

  getCrashPatterns(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/crashpatterns`;
    return this.http.get<any[]>(endPoint);
  }

  getCrashTypeCrashTypeCategories() {
    const endPoint = `${env.api}/investigations/crashtypecrashtypecategories`;
    return this.http.get<any[]>(endPoint);
  }

  getContributingFactorCategories(type: 'contributing' | 'crash') {
    const endPoint = `${env.api}/investigations/cfc/${type}`;
    return this.http.get<IContributingFactorCategory[] | ICrashTypeCategory[]>(
      endPoint
    );
  }

  excludeCrashes(cases: string[], investigationId: number, exclude: boolean) {
    const endPoint = `${env.api}/investigations/${investigationId}/excludecrashes/${exclude}`;
    return this.http.post(endPoint, cases, { responseType: 'text' });
  }

  getAllContributingFactors() {
    const endPoint = `${env.api}/investigations/contributingfactors`;
    return this.http.get<IContributingFactor[]>(endPoint);
  }

  getRefCountermeasures(type: 'contributing' | 'crash', id: number) {
    const endPoint = `${env.api}/investigations/cfc/${type}/${id}`;
    return this.http.get<IRefCountermeasure[]>(endPoint);
  }

  getTestFrequencies() {
    const endPoint = `${env.api}/investigations/testfrequencies`;
    return this.http.get<ITestFrequency[]>(endPoint);
  }

  getTestProportions() {
    const endPoint = `${env.api}/investigations/testproportions`;
    return this.http.get<ITestProportions[]>(endPoint);
  }

  getCountermeasures() {
    const endPoint = `${env.api}/investigations/countermeasures`;
    return this.http.get<ICountermeasure[]>(endPoint);
  }

  getImplementationMetrics() {
    const endPoint = `${env.api}/implementations/metrics`;
    return this.http.get<IImplementationOverviewMetric[]>(endPoint);
  }

  getAccomplishments() {
    const endPoint = `${env.api}/implementations/accomplishments`;
    return this.http.get<IAccomplishment[]>(endPoint);
  }

  getRecommendations(type?: 'capimprovements' | 'initiatives' | 'maintenance') {
    let endPoint = `${env.api}/implementations/recommendations`;
    if (type) {
      endPoint = `${env.api}/implementations/recommendations?type=${type}`;
    }
    return this.http.get<IRecommendation[]>(endPoint);
  }

  getRecommendationsByID(id: number) {
    const endPoint = `${env.api}/investigations/${id}/recommendations`;
    return this.http.get<IRecommendation[]>(endPoint);
  }

  getAllRecommendations() {
    const endPoint = `${env.api}/implementations/recommendations`;
    return this.http.get<IRecommendation[]>(endPoint, {
      headers: this.Headers,
    });
  }

  getCapitalProposals() {
    const endPoint = `${env.api}/implementations/capitalproposals`;
    return this.http.get<ICapitalProposal[]>(endPoint);
  }

  updateImplementationType(ids: number[], type: number) {
    const endPoint = `${env.api}/implementations/recommendations/${type}`;
    return this.http.post<IRecommendation[]>(endPoint, ids);
  }

  deactivateImplementation(id: number) {
    const endPoint = `${env.api}/implementations/deactivate/${id}`;
    return this.http.get<IRecommendation[]>(endPoint);
  }

  getRefCountermeasure(xrefCountermeasureInvestigationID: number) {
    const endPoint = `${env.api}/investigations/${xrefCountermeasureInvestigationID}/recommendations/countermeasure`;
    return this.http.get<IRefCountermeasure>(endPoint);
  }

  addAlternative(alternative: IAlternative, investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/alternatives`;
    return this.http.post<IAlternative>(endPoint, alternative);
  }

  updateAlternativeCountermeasure(
    linkedCountermeasures: ILinkedCountermeasure[],
    investigationID: number
  ) {
    const endPoint = `${env.api}/investigations/alternativecountermeasures`;
    return this.http.put<ILinkedCountermeasure[]>(
      endPoint,
      linkedCountermeasures
    );
  }

  // these contain just IDs and percentages
  getRefAlternativeCountermeasures(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/alternativecountermeasures`;
    return this.http.get<any[]>(endPoint);
  }

  completeInvestigation(comment: string, investigationID: number) {
    const formData = new FormData();
    formData.append('username', sessionStorage.getItem('UserName'));
    const endPoint = `${env.api}/investigations/${investigationID}/complete`;
    return this.http.put<IInvestigation>(endPoint, formData);
  }

  finalizeInvestigation(note: IInvestigationNote, investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/finalize`;
    return this.http.post(endPoint, note);
  }

  approveInvestigation(comment: string, investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/approve`;
    const formData = new FormData();
    formData.append('comment', comment);
    return this.http.put<IInvestigation>(endPoint, formData);
  }

  copyAlternative(
    alternativeID: number,
    investigationID: number,
    newName: string
  ) {
    const endPoint = `${env.api}/investigations/${investigationID}/alternatives/${alternativeID}/copy`;
    const formData = new FormData();
    formData.append('newName', newName);
    return this.http.post<IAlternative>(endPoint, formData);
  }

  removeAlternative(investigationID: number, alternativeID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/alternatives/${alternativeID}`;
    return this.http.delete(endPoint);
  }

  getAlternatives(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/alternatives`;
    return this.http.get<IAlternative[]>(endPoint);
  }

  getAlternativeCountermeasures(alternativeID: number) {
    const endPoint = `${env.api}/investigations/alternativecountermeasures/${alternativeID}`;
    return this.http.get<ILinkedCountermeasure[]>(endPoint);
  }

  getLuts() {
    return new Promise<boolean>((res) => {
      this.http
        .get(env.gapi, { headers: this.GlobalHeaders })
        .pipe(take(1))
        .subscribe(
          (tables) => {
            for (const key of Object.keys(tables)) {
              localStorage.setItem(key, JSON.stringify(tables[key]));
            }
            res(true);
          },
          (err) => {
            console.error(err);
          }
        );
    });
  }

  getSetting(settingName: string) {
    const endPoint = `${env.api}/settings/${settingName}`;
    return this.http.get<number>(endPoint);
  }

  confirmCrash(caseNumber: string, reviewedIndicator: number) {
    const endPoint = `${env.api}/crashes/${caseNumber}/review/${reviewedIndicator}`;
    return this.http.get<IAlternative[]>(endPoint);
  }

  confirmEvalCrash(caseNumber: string, reviewedIndicator: number) {
    const endPoint = `${env.api}/crashes/eval/${caseNumber}/review/${reviewedIndicator}`;
    return this.http.get<IAlternative[]>(endPoint);
  }

  confirmCountermeasureCrash(caseNumber: string, reviewedIndicator: number) {
    const endPoint = `${env.api}/crashes/countermeasure/${caseNumber}/review/${reviewedIndicator}`;
    return this.http.get<IAlternative[]>(endPoint);
  }

  getCrashReports(caseNumber: string) {
    const endPoint = `${env.api}/crashes/${caseNumber}/reports`;
    return this.http.get<any[]>(endPoint);
  }

  makeDotQueueRequest(DOTQueueRequest: any) {
    const endPoint = `${env.api}/crashes/dotqueue`;
    return this.http.post<any>(endPoint, DOTQueueRequest);
  }

  getCrashStatistics(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/crashstatistics`;
    return this.http.get<ICrashStatistic[]>(endPoint);
  }

  getCrashLocationDetails(caseNumber: number) {
    const endPoint = `${env.api}/crashes/${caseNumber}/crashLocationDetails`;
    return this.http.get<ICrashLocationDetail>(endPoint);
  }

  getVehicleDetails(caseNumber: number) {
    const endPoint = `${env.api}/crashes/${caseNumber}/vehicleDetails`;
    return this.http.get<IVehicleDetail[]>(endPoint);
  }

  getCrashSeverityCountsForHomePage() {
    const endPoint = `${env.api}/crashes/home`;
    return this.http.get<any[]>(endPoint);
  }

  getCrashCountsForYear(year: number) {
    const endPoint = `${env.api}/crashes/${year}`;
    return this.http.get<any[]>(endPoint);
  }

  saveAOIS(geometryArray: any[], investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/studyarea`;
    return this.http.post(endPoint, geometryArray);
  }

  loadAOIS(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/studyarea`;
    return this.http.get<IStudyArea[]>(endPoint);
  }

  updateCrashes(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/updateCrashes`;
    return this.http.get(endPoint, { responseType: 'text' });
  }

  saveImage(investigationId: number, image: any, type: 'site' | 'collision') {
    const formData = new FormData();
    formData.append('File', image);
    formData.append('ImageType', type);
    const endPoint = `${env.api}/investigations/${investigationId}/images`;
    return this.http.post(endPoint, formData);
  }

  generateReport(
    investigationID: number,
    coreData: any,
    data: any,
    queryString?: any,
    alternativeData?: any,
    recommendationData?: any,
    countermeasureData?: any,
    crashData?: any,
    charts?: any
  ) {
    const formData = new FormData();
    formData.append('CoreInvestigationData', coreData);
    formData.append('SiteImageIds', JSON.stringify(data.SiteImageIds));
    formData.append(
      'CollisionDiagramIds',
      JSON.stringify(data.CollisionDiagramIds)
    );
    formData.append(
      'CollisionDiagramLegends',
      data.CollisionDiagramLegends
        ? JSON.stringify(data.CollisionDiagramLegends)
        : JSON.stringify([])
    );
    formData.append('RecommendationData', recommendationData);
    formData.append('CountermeasureData', countermeasureData);
    formData.append('AlternativeData', alternativeData);
    formData.append('CrashData', crashData);
    formData.append('ChartData', charts);
    const endPoint = `${env.api}/investigations/${investigationID}/report`;
    return this.http.post(endPoint, formData, {
      responseType: 'arraybuffer',
      params: queryString,
    });
  }

  deleteImage(investigationId: number, imageId: number) {
    const endPoint = `${env.api}/investigations/${investigationId}/images/${imageId}`;
    return this.http.delete(endPoint);
  }

  getImages(investigationId: number) {
    const endPoint = `${env.api}/investigations/${investigationId}/images`;
    return this.http.get<any[]>(endPoint);
  }

  getCollisionDiagrams(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/collisiondiagrams`;
    return this.http.get<ICollisionDiagram[]>(endPoint);
  }

  deleteDiagram(investigationID: number, diagramID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/collisiondiagrams/${diagramID}`;
    return this.http.delete(endPoint);
  }

  getFacilityTypes() {
    const endPoint = `${env.api}/investigations/facilitytypes`;
    return this.http.get<IFacilityType[]>(endPoint).pipe(shareReplay(1));
  }

  getAllFacilityTypes() {
    const endPoint = `${env.api}/investigations/allfacilitytypes`;
    return this.http.get<IFacilityType[]>(endPoint).pipe(shareReplay(1));
  }

  getFacilityTypeById(facilityTypeId: number) {
    const endPoint = `${env.api}/investigations/getFacilityType/${facilityTypeId}`;
    return this.http.get<IRefFacilityType>(endPoint).pipe(shareReplay(1));
  }

  getSavedQueries() {
    const formData = new FormData();
    formData.set('username', sessionStorage.getItem('UserName'));
    const endPoint = `${env.api}/queries`;
    return this.http.post<ISavedQuery[]>(endPoint, formData);
  }

  getQueryAOI(id: string) {
    const endPoint = `${env.api}/queries/${id}/aoi`;
    return this.http.get<any>(endPoint);
  }

  getSummaryStatisticGroups() {
    const endPoint = `${env.api}/investigations/statisticgroups`;
    return this.http.get<ISummaryStatisticGroup[]>(endPoint);
  }

  getCMFS(facilityTypeID: number, countermeasureID: number) {
    const endPoint = `${env.api}/investigations/cmfs/${countermeasureID}/${facilityTypeID}`;
    return this.http.get<ICMF[]>(endPoint);
  }

  getLinkedCMFs(linkedCountermeasureID: number) {
    const endPoint = `${env.api}/investigations/linkedcountermeasures/${linkedCountermeasureID}/cmfs`;
    return this.http.get<ICMF[]>(endPoint);
  }

  saveCMFs(investigationID: number, cmfsToSave: any[]) {
    const endPoint = `${env.api}/investigations/${investigationID}/linkedcountermeasures/cmfs`;
    return this.http.post(endPoint, cmfsToSave);
  }

  getCrashCosts() {
    const endPoint = `${env.api}/investigations/crashcosts`;
    return this.http.get<ICrashCost>(endPoint);
  }

  getCountermeasureCosts(countermeasureID: number) {
    const endPoint = `${env.api}/investigations/countermeasurecosts/${countermeasureID}`;
    return this.http.get<ICountermeasureCost[]>(endPoint);
  }

  saveCountermeasureCosts(countermeasureCosts: ICountermeasureCost[]) {
    const endPoint = `${env.api}/investigations/countermeasurecosts/`;
    return this.http.post(endPoint, countermeasureCosts);
  }

  // these will get costs that have been saved
  getCountermeasureCostsByLinkedCountermeasureID(
    linkedCountermeasureID: number
  ) {
    const endPoint = `${env.api}/investigations/refcountermeasurecosts/${linkedCountermeasureID}`;
    return this.http.get<ICountermeasureCost[]>(endPoint);
  }

  // Get Major and Minor road volumes for a given MasterIntersectionID
  getIntersectionVolumes(MasterIntersectionID: number) {
    const endPoint = `${env.api}/investigations/intersectionVolumes/${MasterIntersectionID}`;
    return this.http.get<IIntersectionVolume>(endPoint);
  }

  exportShapefile(exportScenarioResults: any) {
    const endPoint = `${env.api}/geometry`;
    return this.http.post(endPoint, exportScenarioResults, {
      responseType: 'blob',
    });
  }

  getSPFs() {
    const endPoint = `${env.api}/investigations/spfs`;
    return this.http.get<SPFInfo[]>(endPoint);
  }

  getStatewideAverages() {
    const endPoint = `${env.api}/investigations/StatewideAverages`;
    return this.http.get<refStatewideAverages[]>(endPoint);
  }

  getStatewideProportionsList() {
    const endPoint = `${env.api}/investigations/StatewideProportions`;
    return this.http.get<refStatewideProportions[]>(endPoint);
  }

  getNotes(investigationID: number) {
    const endPoint = `${env.api}/investigations/${investigationID}/notes`;
    return this.http.get<IInvestigationNote[]>(endPoint);
  }

  saveNote(note: IInvestigationNote) {
    const endPoint = `${env.api}/investigations/notes`;
    return this.http.post<IInvestigationNote>(endPoint, note);
  }

  deleteNote(noteID: number) {
    const endPoint = `${env.api}/investigations/notes/${noteID}`;
    return this.http.delete(endPoint);
  }

  getTables() {
    const endPoint = `${env.api}/settings/tables`;
    return this.http.get<any[]>(endPoint);
  }

  getSystemSettings() {
    this.http
      .get<ISystemSetting[]>(`${env.api}/settings`)
      .pipe(take(1))
      .subscribe((settings) => {
        for (const key of Object.keys(settings)) {
          localStorage.setItem(
            settings[key].Name,
            JSON.stringify(settings[key])
          );
        }
      });
  }

  getSafetySettings() {
    return new Promise((res) => {
      this.http
        .get<ISystemSetting[]>(`${env.api}/settings/SafetySettings`)
        .pipe(take(1))
        .subscribe((settings) => {
          for (const key of Object.keys(settings)) {
            localStorage.setItem(
              `SAFETY_${settings[key].Name}`,
              JSON.stringify(settings[key])
            );
          }
          res(true);
        });
    });
  }

  //Home News Api Calls
  GetAdminContacts() {
    const endPoint = `${env.api}/Home/GetAdminContacts`;
    return this.http.get<IAdminContacts[]>(endPoint);
  }

  GetNewsText() {
    const endPoint = `${env.api}/Home/GetNewsText`;
    return this.http.get<INewsText[]>(endPoint);
  }

  GetNewFeeds() {
    const endPoint = `${env.api}/Home/GetNewsFeed`;
    return this.http.get<IChannel>(endPoint);
  }

  GetInvestigationRegionStats() {
    const endPoint = `${env.api}/Home/GetInvestigationRegionStats`;
    return this.http.get<IInvestigationRegionStats[]>(endPoint);
  }

  crashReport(caseNum: number) {
    window.open(`${env.reportURL}${caseNum}`, '_blank');
  }

  GetSafetyUsers() {
    const endPoint = `${env.api}/Users/GetSafetyUsers/${sessionStorage.getItem(
      'PortalToken'
    )}`;
    return this.http.get<any[]>(endPoint);
  }

  GetSafetyUsersWithIDs() {
    const endPoint = `${
      env.api
    }/Users/GetSafetyUsersWithIDs/${sessionStorage.getItem('PortalToken')}`;
    return this.http.get<IUserWithID[]>(endPoint);
  }

  SetAnnualScreening(AnnualScreening: IRegionAnnualScreening) {
    const endPoint = `${env.api}/scenarios/SetAnnualScreening`;
    return this.http.post<IAnnualResults>(endPoint, AnnualScreening, {
      headers: this.Headers,
    });
  }

  getScreeningSegmentInfo(
    SiteInfo: any,
    YearFrom: string,
    YearTo: string,
    RIYear: string
  ) {
    const endPoint = `${env.screeningService}?SegmentID=${
      SiteInfo.SEGMENT_ID
    }&YearRangeFrom=${YearFrom}&YearRangeTo=${YearTo}&CrashSeverities&FromMeasure=${
      SiteInfo.from_measure
    }&ToMeasure=${
      SiteInfo.to_measure
    }&PerformanceMeasures=1%2C5&RIyear=${RIYear}&f=json&token=${sessionStorage.getItem(
      'PortalToken'
    )}`;
    return this.http.get<any>(endPoint);
  }

  getScreeningInfo(
    SiteInfo: any,
    YearFrom: string,
    YearTo: string,
    RIYear: string
  ) {
    let endPoint = `${env.screeningService}`;
    if (SiteInfo.SEGMENT_ID) {
      endPoint = endPoint + `?SegmentID=${SiteInfo.SEGMENT_ID}`;
    } else if (SiteInfo.RampID) {
      endPoint = endPoint + `?RampID=${SiteInfo.RampID}`;
    } else if (SiteInfo.IntersectionID) {
      endPoint = endPoint + `?IntersectionID=${SiteInfo.IntersectionID}`;
    }
    endPoint =
      endPoint +
      `&YearRangeFrom=${YearFrom}&YearRangeTo=${YearTo}&CrashSeverities`;
    if (SiteInfo.from_measure && SiteInfo.to_measure) {
      endPoint =
        endPoint +
        `&FromMeasure=${SiteInfo.from_measure}&ToMeasure=${SiteInfo.to_measure}`;
    }
    endPoint =
      endPoint +
      `&PerformanceMeasures=1%2C5&RIyear=${RIYear}&f=json&token=${sessionStorage.getItem(
        'PortalToken'
      )}`;
    return this.http.get<any>(endPoint);
  }

  GetUserRequestData(Username: string) {
    return this.http.get(env.api + '/users/GetUserRequestData/' + Username);
  }

  InsertCrashNumbersForBatchReporting(CaseNumberList: string[]) {
    return this.http.post(
      env.gapi_root + 'CrashRef/GetCrashNumberReportViewer/InsertCaseNumber/',
      CaseNumberList,
      { headers: this.GlobalHeaders }
    );
  }
}
